import {
  Box,
  Button,
  Divider,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowsOut } from '@phosphor-icons/react';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Position,
  useEdges,
  useReactFlow,
  useUpdateNodeInternals,
} from 'reactflow';
import { useAuthStore } from '../../store/storeAuth';
import { NodeProps } from '../../util/Types/NodeProps';
import { isActiveEdge } from '../../util/findActiveHandle';
import InputHandle from '../UI/InputHandle/InputHandle';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';

import { styled } from 'styled-components';
import { useNodeResize } from '../../hooks/useNodeResize';
import { axiosInstance } from '../../service/ApiService';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { modifyData } from '../../util/autosave/changeTypes';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { executeErrorHandle } from '../../util/executeErrorHandle';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';
import { TextComponent } from '../UI/PreviewModal/PreviewModal';

const saveTypes = [
  {
    name: 'Save as .txt',
    extension: '.txt',
    fileType: 'txt',
    mimeType: 'text/plain',
  },
  {
    name: 'Save as .html',
    extension: '.html',
    fileType: 'html',
    mimeType: 'text/html',
  },
  {
    name: 'Save as .json',
    extension: '.json',
    fileType: 'json',
    mimeType: 'application/json',
  },
  {
    name: 'Save as .csv',
    extension: '.csv',
    fileType: 'csv',
    mimeType: 'text/csv',
  },
  {
    name: 'Save as .xml',
    extension: '.xml',
    fileType: 'xml',
    mimeType: 'application/xml',
  },
  {
    name: 'Save as .yaml',
    extension: '.yaml',
    fileType: 'yaml',
    mimeType: 'text/yaml',
  },
  {
    name: 'Save as .pdf',
    extension: '.pdf',
    fileType: 'pdf',
    mimeType: 'application/pdf',
  },
  {
    name: 'Save as .docx',
    extension: '.docx',
    fileType: 'docx',
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  {
    name: 'Save as .png',
    extension: '.png',
    fileType: 'png',
    mimeType: 'image/png',
  },
  {
    name: 'Save as .jpg',
    extension: '.jpg',
    fileType: 'jpg',
    mimeType: 'image/jpeg',
  },
  {
    name: 'Save as .webp',
    extension: '.webp',
    fileType: 'webp',
    mimeType: 'image/webp',
  },
];

const DEFAULT_WIDTH = 800;
const DEFAULT_HEIGHT = 600;
const MIN_WIDTH = 200;
const MIN_HEIGHT = 200;

const BlueSwitch = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: 'rgba(54, 80, 222, 1)',
  },
}));

interface FileLinkData {
  src: string;
  name: string;
}

function getInitialSavingTypeByFileLinkName(extension: string | undefined) {
  if (extension) {
    const savedType = saveTypes.find(
      saveType => saveType.extension === extension,
    );

    if (savedType) {
      return savedType.name;
    }
  }

  return saveTypes[0].name;
}

function OutputControllerNode({
  data,
  type,
  id,
  selected,
  width,
  height,
}: NodeProps) {
  const edges = useEdges();
  const { setSaving, flowId, spaceId, setSchema } = useFlowsStore(state => state);

  const { setNodes: setNodeState } = useReactFlow();

  const updateNodeInternals = useUpdateNodeInternals();

  const user = useAuthStore(state => state.user);
  const setNotification = useNotificationStore(state => state.setNotification);

  const [isLoading, setIsLoading] = useState(false);
  const [hasToExecute, setHasToExecute] = useState(true);
  const [executeCounter, setExecuteCounter] = useState(0);
  const [savingType, setSavingType] = useState(
    getInitialSavingTypeByFileLinkName(data?.fileExtension),
  );
  const [filter, setFilter] = useState(data.filter || 'all');
  const [saveOutput, setSaveOutput] = useState(data?.saveOutput ?? false);
  const [fileLink, setFileLink] = useState<FileLinkData | undefined>(
    data?.fileLink || undefined,
  );
  const [useDocraptor, setUseDocraptor] = useState(data?.useDocraptor ?? false);
  const [customFileName, setCustomFileName] = useState(
    data?.customFileName || '',
  );

  const debounceTimeoutRef = useRef<any>(null);
  const savingTypeRef = useRef<null | string>(savingType);

  useEffect(() => {
    if (data?.fileLink) {
      setFileLink(data.fileLink);
    }
  }, [data?.fileLink]);

  useEffect(() => {
    // customFileName is already updated directly by modifyData via nodePassOnlyText
    // Just sync to local state for the text input display
    if (data?.customFileName !== undefined) {
      setCustomFileName(data.customFileName);
    }
  }, [data?.customFileName]);

  const onExecute = useCallback(async () => {
    try {
      setIsLoading(true);

      const inputData = data?.textData || [];
      const processedData = inputData.map((item: any) => item.text).join('\n');

      // Read customFileName directly from data property (set by backend via nodePassOnlyText)
      const currentCustomFileName = data?.customFileName || '';

      if (processedData.includes('<canvas')) {
        setNotification({
          type: 'warning',
          message:
            "Your content contains <canvas> elements, which aren't currently supported. Try to create your charts using d3.js library in HTML.",
        });
      }

      let fileLinkResponse: FileLinkData | undefined = undefined;

      if (saveOutput) {
        const findType = saveTypes.find(type => type.name === savingType);

        if (findType) {
          let endpoint = '/upload/text';
          if (findType.fileType === 'pdf' && useDocraptor) {
            endpoint = '/upload/text-docraptor';
          }

          const requestBody: {
            text: string;
            fileType: string;
            fileName?: string;
            spaceId?: string;
            flowId?: string;
          } = {
            text: processedData || '',
            fileType: findType.fileType,
            spaceId,
            flowId,
          };

          if (currentCustomFileName) {
            requestBody.fileName = currentCustomFileName;
          }

          try {
            const { data: res } = await axiosInstance.post(
              endpoint,
              requestBody,
            );

            fileLinkResponse = res.data as FileLinkData;
            setFileLink(fileLinkResponse);
            window.dispatchEvent(new CustomEvent('sidebar-refresh'));
          } catch (uploadError: unknown) {
            const errorMessage =
              uploadError instanceof Error
                ? uploadError.message
                : 'Failed to generate file link';
            setNotification({
              type: 'error',
              message: errorMessage,
            });
          }
        }
      }

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        setSaving,
        flowTriggerData: checkExecuteTrigger(data, id),
        changeType: 'execute',
        objectData: {
          text: processedData,
          textData: inputData,
          fileLink: fileLinkResponse,
          fileExtension: savingTypeRef.current
            ? saveTypes.find(type => type.name === savingTypeRef.current)?.fileType
            : data?.fileExtension,
          saveOutput,
        },
        objectCallerData: [
          ...(user?.spaceObjects || []),
          ...(user?.objects || []),
        ],
        setSchema,
      });

      if (fileLinkResponse) {
        setTimeout(() => {
          setNodeState((currentNodes: any[]) => {
            const updatedNodes = currentNodes.map(node => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    fileLink: fileLinkResponse,
                  },
                };
              }
              return node;
            });

            return modifyData(
              updatedNodes,
              id,
              { fileLink: fileLinkResponse } as any,
              edges,
              'changeText',
              [...(user?.spaceObjects || []), ...(user?.objects || [])],
            );
          });
        }, 100);
      }

      const contentOutEdge = edges?.find(
        edge => edge?.source === id && edge?.sourceHandle === 'contentOut',
      );

      if (contentOutEdge) {
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id: contentOutEdge.target,
          flowId,
          setSaving,
          flowTriggerData: checkExecuteTrigger(data, id),
          changeType: 'execute',
          objectData: {
            text: processedData,
            textData: inputData,
          },
          setSchema,
        });
      }
    } catch (error) {
      executeErrorHandle(
        executeCounter,
        setExecuteCounter,
        setHasToExecute,
        data,
        edges,
        setNodeState,
        setSaving,
        id,
        flowId || '',
        setSchema,
        setNotification,
        error,
        [...(user?.spaceObjects || []), ...(user?.objects || [])],
      );
    } finally {
      setIsLoading(false);
      setHasToExecute(true);
    }
  }, [
    data,
    edges,
    executeCounter,
    flowId,
    id,
    savingType,
    saveOutput,
    useDocraptor,
    setNodeState,
    setNotification,
    setSaving,
    setSchema,
    user?.objects,
    user?.spaceObjects,
  ]);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          onExecute();
        }, 2000);
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [data?.flowTrigger, hasToExecute, data?.canceled, onExecute]);

  const onChangeSavingType = useCallback(
    (e: { target: { value: React.SetStateAction<string> } }) => {
      setSavingType(e.target.value);
      savingTypeRef.current = e.target.value as string;
    },
    [setSavingType],
  );

  const onUseDocraptorChange = () => {
    setUseDocraptor((prev: boolean) => !prev);
    updateNodeInternals(id);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      objectCallerData: user?.objects,
      objectData: {
        useDocraptor: !useDocraptor,
      },
      setSchema,
      setSaving,
    });
  };

  const onSaveOutputChange = async () => {
    const newSaveOutputValue = !saveOutput;
    setSaveOutput(newSaveOutputValue);
    updateNodeInternals(id);

    const findType = saveTypes.find(type => type.name === savingType);

    const response = data?.textData
      ?.map((item: { text: string }) => item.text)
      .join(' \n');

    // Read customFileName directly from data property
    const currentCustomFileName = data?.customFileName || '';

    if (!findType) {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        objectCallerData: user?.objects,
        objectData: {
          saveOutput: newSaveOutputValue,
        },
        setSchema,
        setSaving,
      });
      return;
    }

    if (!response) {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        objectCallerData: user?.objects,
        objectData: {
          saveOutput: newSaveOutputValue,
        },
        setSchema,
        setSaving,
      });
      return;
    }

    let endpoint = '/upload/text';
    if (findType.fileType === 'pdf' && useDocraptor) {
      endpoint = '/upload/text-docraptor';
    }

    const requestBody: {
      text: string;
      fileType: string;
      fileName?: string;
      spaceId?: string;
      flowId?: string;
    } = {
      text: response || '',
      fileType: findType.fileType,
      spaceId,
      flowId,
    };

    if (currentCustomFileName) {
      requestBody.fileName = currentCustomFileName;
    }

    try {
      const { data: res } = await axiosInstance.post(endpoint, requestBody);

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        objectCallerData: user?.objects,
        objectData: {
          saveOutput: newSaveOutputValue,
          fileLink: res.data,
        },
        setSchema,
        setSaving,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to generate file link';
      setNotification({
        type: 'error',
        message: errorMessage,
      });

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        objectCallerData: user?.objects,
        objectData: {
          saveOutput: newSaveOutputValue,
        },
        setSchema,
        setSaving,
      });
    }
  };

  const onChangeFilter = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setFilter(event.target.value);
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      changeType: 'changeText',
      flowId,
      objectData: {
        filter: event.target.value,
      },
      setSchema,
    });
  };

  const saveData = useCallback(
    (payload: any, changeType: string = 'default') => {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        objectData: payload,
        setSchema,
        changeType,
      });
    },
    [edges, flowId, id, setNodeState, setSaving, setSchema],
  );

  const {
    nodeWidth,
    nodeHeight,
    handleResize,
    handleResizeEnd,
    handleResizeStart,
  } = useNodeResize({
    id,
    data,
    width: width ?? undefined,
    height: height ?? undefined,
    defaultWidth: DEFAULT_WIDTH,
    defaultHeight: DEFAULT_HEIGHT,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    onResizeEnd: useCallback(
      (finalWidth: number, finalHeight: number) => {
        saveData({ size: { width: finalWidth, height: finalHeight } });
      },
      [saveData],
    ),
  });

  const onCopy = (text: string) => {
    navigator.clipboard.writeText(text);

    setNotification({
      type: 'success',
      message: 'Text copied to clipboard',
    });
  };

  const onCustomFileNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setCustomFileName(value);

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        objectCallerData: user?.objects,
        objectData: {
          customFileName: value,
        },
        setSchema,
        setSaving,
      });
    },
    [edges, flowId, id, setNodeState, setSaving, setSchema, user?.objects],
  );

  useDebounceEffect(
    () => {
      const nextExtension = saveTypes.find(
        type => type.name === savingType,
      )?.extension;
      if (nextExtension === data?.fileExtension) {
        return;
      }
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        changeType: 'changeText',
        flowId,
        objectData: {
          fileExtension: nextExtension,
        },
        setSchema,
      });
    },
    [savingType, data?.fileExtension],
    500,
  );

  useDebounceEffect(
    () => {
      if (useDocraptor === (data?.useDocraptor ?? false)) {
        return;
      }
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        changeType: 'changeText',
        flowId,
        objectData: {
          useDocraptor: useDocraptor,
        },
        setSchema,
      });
    },
    [useDocraptor, data?.useDocraptor],
    500,
  );

  const onDownload = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();

      if (!savingTypeRef.current) return;

      const findType = saveTypes.find(
        type => type.name === savingTypeRef.current,
      );

      if (!findType) {
        return;
      }

      const response = data?.textData
        ?.map((item: { text: string }) => item.text)
        .join(' \n');

      // Read customFileName directly from data property
      const currentCustomFileName = data?.customFileName || '';

      try {
        let endpoint = '/upload/text';
        if (findType?.fileType === 'pdf' && useDocraptor) {
          endpoint = '/upload/text-docraptor';
        }

        const requestBody: {
          text: string;
          fileType: string;
          fileName?: string;
          spaceId?: string;
          flowId?: string;
        } = {
          text: response || '',
          fileType: findType?.fileType,
          spaceId,
          flowId,
        };

        if (currentCustomFileName) {
          requestBody.fileName = currentCustomFileName;
        }

        const {
          data: { data: fileLinkResponse },
        } = await axiosInstance.post(endpoint, requestBody);

        const { data: res } = await axiosInstance.get('/upload/presigned-url', {
          params: {
            url: fileLinkResponse.src,
          },
        });

        const urlContentBlob = new Blob(
          [new Uint8Array(res.data.file.data).buffer],
          {
            type: findType.mimeType,
          },
        );

        const url = URL.createObjectURL(urlContentBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileLinkResponse.name;
        document.body.appendChild(link);
        link.click();
      } catch (error: any) {
        setNotification({ type: 'error', message: error.message });
      }
    },
    [data?.textData, data?.customFileName, useDocraptor, setNotification],
  );

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;

  return (
    <Node
      id={id}
      data={data}
      type={type}
      btnText="Run"
      onSubmit={onExecute}
      isLoading={isLoading}
      edges={edges}
    >
      <Box
        sx={{
          display: 'grid',
          gap: '0.5rem',
          gridTemplateRows: '3fr min-content min-content',
          height: `${nodeHeight}px`,
          width: `${nodeWidth}px`,
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateRows: 'min-content 3fr min-content',
            overflow: 'hidden',
          }}
          p="16px"
          className="nowheel"
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
            }}
          >
            <Box>
              <FormLabel
                sx={{
                  color:
                    user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)',
                }}
              >
                Save output and generate file link.
              </FormLabel>
              <BlueSwitch
                value={'Monthly'}
                checked={saveOutput}
                onChange={onSaveOutputChange}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </Box>

            <Select
              value={filter}
              onChange={onChangeFilter}
              className="nodrag"
              size="small"
              sx={{
                '& .MuiList-root': {
                  backgroundColor:
                    user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                },
                '& .MuiSvgIcon-root': {
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                },
                borderRadius: '8px',
                border: 'none',
                boxShadow: 'none',
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
                bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .Mui-selected': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                    },
                    backgroundColor:
                      user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  },
                },
              }}
            >
              <MenuItem
                value="all"
                sx={{
                  color:
                    user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)',
                }}
              >
                Intake all
              </MenuItem>
              <MenuItem
                value="code"
                sx={{
                  color:
                    user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)',
                }}
              >
                Only Intake 'code'
              </MenuItem>
              <MenuItem
                value="table"
                sx={{
                  color:
                    user?.theme === 'dark' ? '#FFF' : 'rgba(71, 84, 103, 1)',
                }}
              >
                Only Intake 'tables'
              </MenuItem>
            </Select>
          </Box>

          <Box
            sx={{
              overflow: 'auto',
              width: '100%',
              minHeight: 0,
              height: '100%',
            }}
          >
            {data?.textData?.map((item: any) => (
              <TextComponent
                key={item?.text?.slice(0, 100) + item.id}
                item={item}
                user={user}
                onCopy={onCopy}
                contentType={filter}
              />
            ))}
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Button variant="contained" onClick={onDownload}>
              Download
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {savingType === 'Save as .pdf' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormLabel
                    sx={{
                      color:
                        user?.theme === 'dark'
                          ? '#FFF'
                          : 'rgba(71, 84, 103, 1)',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Use DocRaptor
                  </FormLabel>
                  <BlueSwitch
                    value={'Docraptor'}
                    checked={useDocraptor}
                    onChange={onUseDocraptorChange}
                    inputProps={{ 'aria-label': 'docraptor-controlled' }}
                  />
                </Box>
              )}
              <Select
                onChange={onChangeSavingType}
                value={savingType}
                name="saveTypes"
                className="nodrag"
                sx={{
                  height: 'unset',
                  '& .MuiList-root': {
                    backgroundColor:
                      user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  },
                  bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  border: `2px solid ${
                    user?.theme === 'dark' ? '#475467' : '#EAECF0'
                  }`,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .Mui-selected': {
                        backgroundColor:
                          user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                      },
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    },
                  },
                  style: {
                    maxHeight: '700px',
                  },
                }}
              >
                {saveTypes.map((saveType, index) => (
                  <MenuItem key={index} value={saveType.name}>
                    <Typography
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                      }}
                    >
                      {saveType.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box p="22px">
          {saveOutput && (
            <>
              <Box
                position="relative"
                mb="16px"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <InputHandle
                  activeColor={findColor?.color}
                  handleId="customFileNameIn"
                  isActive={isActiveEdge(
                    edges,
                    id,
                    'customFileNameIn',
                    'target',
                  )}
                  left={-32}
                  top={'50%'}
                  position={Position.Left}
                  type="target"
                />
                <Typography
                  fontSize="16px"
                  color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
                  fontWeight={600}
                  mb="8px"
                >
                  Custom File Name
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter custom file name"
                  value={customFileName}
                  onChange={onCustomFileNameChange}
                  disabled={isActiveEdge(
                    edges,
                    id,
                    'customFileNameIn',
                    'target',
                  )}
                  className="nodrag"
                  size="small"
                  sx={{
                    '& .MuiInputBase-input': {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `1px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                    borderRadius: '4px',
                    maxWidth: '200px',
                  }}
                />
              </Box>
              <OutputTextarea
                value={fileLink ? fileLink.src : 'No Link generated'}
                activeHandle={isActiveEdge(edges, id, 'fileLink', 'source')}
                labelName="Link"
                handleId="fileLink"
                nodeLabel={data?.label}
                type="source"
                findColor={findColor}
                nodeId={id}
                inputSize={data?.fileLinkSize}
                collapsed={data?.collapsed || false}
                style={{
                  marginBottom: 0,
                  height: 'auto',
                }}
              />
            </>
          )}
        </Box>
        <InputHandle
          activeColor={findColor?.color}
          handleId="contentIn"
          isActive={isActiveEdge(edges, id, 'contentIn', 'target')}
          left={-12}
          top={'50%'}
          position={Position.Left}
          type="target"
        />

        <InputHandle
          activeColor={findColor?.color}
          handleId="contentOut"
          isActive={isActiveEdge(edges, id, 'contentOut', 'source')}
          right={-12}
          top={'50%'}
          position={Position.Right}
          type="source"
        />

        {selected && (
          <Box
            onMouseDown={handleResizeStart}
            className="nodrag"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: findColor?.color || '#3650DE',
              borderRadius: '4px',
              cursor: 'nwse-resize',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              zIndex: 1000,
              '&:hover': {
                backgroundColor: findColor?.color || '#3650DE',
                opacity: 0.9,
                transform: 'scale(1.1)',
              },
            }}
          >
            <ArrowsOut
              size={14}
              color="#fff"
              weight="bold"
              style={{ transform: 'rotate(45deg)' }}
            />
          </Box>
        )}
      </Box>
      {/*
      <Box>
        <DataVisualizationModal
          resData={''}
          showDataModal={showDataModal}
          setShowDataModal={setShowDataModal}
        />
      </Box>
         */}
    </Node>
  );
}

export default memo(OutputControllerNode);
